/* ============================================================
   ENGRAM · página de coleção (ecrãs 1l e 1r) — carregado por sections/engram-collection-header.liquid
   Liga o cabeçalho Engram à grelha do tema (sections/main-collection-product-grid), sem a reescrever:
   1. folhas (telemóvel) e gaveta (desktop): filtros (1r-01, 1r-05) e ordenar (1r-02) — abrir, fechar, foco, Esc
   2. filtros: cada toque pede a contagem à Shopify («Mostrar n peças»); preço em faixas que se desmarcam
   3. aplicar filtros e ordem: troca o cabeçalho e a grelha pela Section Rendering API, com cartões esqueleto
      enquanto carrega (1r-04), e escreve o endereço; «voltar» do browser repõe o cabeçalho
   4. quick-add (1r-05): «Adicionar ao saco · preço» no cartão junta a peça e abre o saco do tema
   5. «8 de 24 peças» + «Mostrar mais 8 peças» junto ao botão «carregar mais» do tema
   A página presa no telemóvel é a de assets/engram-search.js (lock/unlock com dono 'filters'): não há outra.
   O cabeçalho é trocado inteiro depois de cada filtro, por isso tudo vai por delegação no document.
   Textos vêm do Liquid em data-*; aqui não há strings.
   ============================================================ */
(() => {
  if (window.__engramCollection) return;
  window.__engramCollection = true;

  const html = document.documentElement;
  const MOBILE = window.matchMedia('(max-width: 920px)'); /* o mesmo corte do header do telemóvel */
  const root = () => document.querySelector('[data-engram-collection]');
  const grid = () => document.querySelector('.collection-page-section, .main-search'); /* 1s: a grelha da pesquisa também */
  const sectionIdOf = (element) => element.id.replace('shopify-section-', '');

  /* durações dos tokens de assets/engram.css (com «menos movimento» ligado valem 1ms) */
  const duration = (name, fallback) => {
    const value = parseFloat(getComputedStyle(html).getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  };

  const say = (text) => {
    const status = root()?.querySelector('[data-engram-status]');
    if (status) status.textContent = text || '';
  };

  /* ---------- 1 · Folhas e gaveta ---------- */

  /* Página presa: no telemóvel, a receita da pesquisa e do menu (body fixo, header no topo, barras de cima fora,
     scroll reposto ao soltar), partilhada por donos. No desktop basta não deixar a página deslizar por trás. */
  const search = () => {
    const element = document.querySelector('engram-search');
    return element && typeof element.lock === 'function' ? element : null;
  };
  const lockPage = () => {
    if (MOBILE.matches && search()) search().lock('filters');
    else html.classList.add('engram-collection-lock');
  };
  const unlockPage = () => {
    if (search()) search().unlock('filters');
    html.classList.remove('engram-collection-lock');
  };

  /* Prender a página sem ela mexer por trás da folha: a prisão partilhada tira as barras de cima e fixa o header,
     o que empurrava o conteúdo para cima (na pesquisa e no menu não se vê, porque a página fica escondida; na folha
     de ordenar a página continua à vista, escurecida). Mede-se o <main> antes e depois e acerta-se o topo do body —
     a prisão repõe o scroll pelo valor que guardou, por isso soltar continua igual. */
  const lockSteady = () => {
    const main = document.querySelector('main');
    const before = main ? main.getBoundingClientRect().top : 0;
    lockPage();
    const body = document.body;
    if (!main || body.style.position !== 'fixed') return;
    const shift = before - main.getBoundingClientRect().top;
    if (shift) body.style.top = `${(parseFloat(body.style.top) || 0) + shift}px`;
  };

  /* filtros: por baixo do header do telemóvel (medido depois de prender); ordenar: o ecrã todo, com o véu por cima do header */
  const place = (panel, underHeader) => {
    const header = document.querySelector('#shopify-section-mobile-header .mobile-header') || document.querySelector('#shopify-section-mobile-header');
    const bottom = underHeader && MOBILE.matches && header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    panel.style.setProperty('--engram-sheet-top', `${Math.round(bottom)}px`);
  };

  let current = null; /* { name, panel, trigger, snapshot, closing, timer } */

  const focusables = (panel) =>
    Array.from(panel.querySelectorAll('button, [href], input, select, summary, [tabindex]:not([tabindex="-1"])')).filter(
      (element) => !element.disabled && element.offsetParent !== null
    );

  /* o estado da folha de filtros ao abrir: fechar sem «Mostrar» volta a ele */
  const snapshot = (panel) => {
    const form = panel.querySelector('[data-engram-filters-form]');
    if (!form) return null;
    const submit = form.querySelector('[data-engram-submit]');
    const active = form.querySelector('[data-engram-active]');
    const inputs = Array.from(form.querySelectorAll('.engram-filters__input'));
    return {
      checked: inputs.map((input) => input.checked),
      disabled: inputs.map((input) => input.disabled),
      submit: submit ? submit.textContent : '',
      submitDisabled: submit ? submit.disabled : false,
      active: active ? active.textContent : '',
    };
  };
  const restore = (panel, state) => {
    const form = panel.querySelector('[data-engram-filters-form]');
    if (!form || !state) return;
    form.querySelectorAll('.engram-filters__input').forEach((input, index) => {
      input.checked = state.checked[index];
      input.disabled = state.disabled[index];
    });
    const submit = form.querySelector('[data-engram-submit]');
    if (submit) {
      submit.textContent = state.submit;
      submit.disabled = state.submitDisabled;
      submit.removeAttribute('aria-busy');
    }
    const active = form.querySelector('[data-engram-active]');
    if (active) active.textContent = state.active;
  };

  const open = (name, trigger) => {
    const panel = root()?.querySelector(`[data-engram-panel="${name}"]`);
    if (!panel) return;
    if (current) closeNow();
    /* ordenar no desktop: painel por baixo do botão, como os menus do header — sem véu nem página presa */
    const popover = name === 'sort' && !MOBILE.matches;
    if (!popover) {
      /* a folha de filtros ocupa o ecrã até ao header: a página por baixo fica invisível depois de ela entrar.
         A de ordenar é baixa: a página fica à vista, escurecida (ecrã 1r-02). */
      const cover = name === 'filters';
      lockSteady();
      place(panel, cover);
      html.classList.add('engram-sheet-open');
      html.classList.toggle('engram-sheet-cover', cover);
    }
    panel.hidden = false;
    void panel.offsetWidth; /* entra no ecrã antes de ganhar .is-open, para a transição correr */
    panel.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    current = { name, panel, trigger, popover, snapshot: snapshot(panel), closing: false, timer: 0 };
    const price = panel.querySelector('input[name="engram-price"]:checked');
    lastPrice = price ? price.value : null;
    panel.querySelector('.engram-sheet__panel')?.focus({ preventScroll: true });
  };

  /* Fecha com o movimento inverso. applied: fechou-se com «Mostrar» (não se repõe a folha).
     behind: outra coisa entra por cima (menu, pesquisa, saco) — a folha fica quieta e sai de uma vez quando já está tapada. */
  const close = ({ returnFocus = true, behind = false, applied = false } = {}) =>
    new Promise((resolve) => {
      if (!current || current.closing) {
        resolve();
        return;
      }
      const state = current;
      state.closing = true;
      if (state.trigger) {
        state.trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) state.trigger.focus({ preventScroll: true });
      }
      if (!behind) {
        html.classList.remove('engram-sheet-cover'); /* a página volta a ver-se por baixo enquanto a folha sai */
        state.panel.classList.remove('is-open');
      }
      state.timer = setTimeout(() => {
        state.panel.classList.remove('is-open');
        state.panel.hidden = true;
        if (!applied) restore(state.panel, state.snapshot);
        if (current === state) current = null;
        if (!state.popover) {
          html.classList.remove('engram-sheet-open', 'engram-sheet-cover');
          unlockPage();
        }
        resolve();
      }, state.popover ? duration('--duration-base', 240) : duration('--duration-slow', 420));
    });

  /* sem movimento: trocar de folha, ou o ecrã passou o corte telemóvel/desktop */
  const closeNow = () => {
    if (!current) return;
    clearTimeout(current.timer);
    current.panel.classList.remove('is-open');
    current.panel.hidden = true;
    if (current.trigger) current.trigger.setAttribute('aria-expanded', 'false');
    restore(current.panel, current.snapshot);
    const popover = current.popover;
    current = null;
    if (!popover) {
      html.classList.remove('engram-sheet-open', 'engram-sheet-cover');
      unlockPage();
    }
  };

  document.addEventListener('keydown', (event) => {
    if (!current || current.closing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab' || current.popover) return; /* o painel do desktop não prende o foco: é um menu, não um diálogo */
    const list = focusables(current.panel);
    if (!list.length) return;
    const first = list[0];
    const last = list[list.length - 1];
    if (event.shiftKey && (document.activeElement === first || !current.panel.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  let wasMobile = MOBILE.matches;
  window.addEventListener('resize', () => {
    if (MOBILE.matches !== wasMobile) {
      wasMobile = MOBILE.matches;
      closeNow();
      return;
    }
    if (current && !current.closing) place(current.panel, current.name === 'filters');
  });

  /* o saco, o menu ou a pesquisa abrem por cima de uma folha aberta: a folha sai por baixo deles */
  document.addEventListener('cart-drawer:open', () => { if (current) close({ returnFocus: false, behind: true }); });

  /* ---------- 2 · Filtros: contagem ao vivo ---------- */

  let lastPrice = null;
  let countTimer = 0;
  let countRequest = 0;

  const currentSort = () => new URLSearchParams(window.location.search).get('sort_by');

  const serialize = (form) => {
    const params = new URLSearchParams(form.dataset.keep || ''); /* 1s: na pesquisa, o q=… fica */
    form.querySelectorAll('input[type="checkbox"].engram-filters__input:checked').forEach((input) => params.append(input.name, input.value));
    const price = form.querySelector('input[name="engram-price"]:checked');
    if (price) {
      const group = price.closest('[data-engram-price]');
      if (price.dataset.min) params.append(group.dataset.gte, price.dataset.min);
      if (price.dataset.max) params.append(group.dataset.lte, price.dataset.max);
    }
    const sort = currentSort();
    if (sort) params.set('sort_by', sort);
    return params.toString();
  };

  const withParams = (base, params, extra) => {
    const query = [params, extra].filter(Boolean).join('&');
    return query ? `${base}?${query}` : base;
  };

  const updateCount = async (form) => {
    const element = root();
    if (!element) return;
    const request = ++countRequest;
    const submit = form.querySelector('[data-engram-submit]');
    if (submit) submit.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(withParams(element.dataset.url, serialize(form), `section_id=${element.dataset.sectionId}`));
      if (!response.ok) throw new Error(String(response.status));
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (request !== countRequest) return;
      const fresh = doc.querySelector('[data-engram-filters-form]');
      if (!fresh) return;
      const freshSubmit = fresh.querySelector('[data-engram-submit]');
      if (submit && freshSubmit) {
        submit.textContent = freshSubmit.textContent;
        submit.disabled = freshSubmit.disabled;
      }
      const active = form.querySelector('[data-engram-active]');
      const freshActive = fresh.querySelector('[data-engram-active]');
      if (active && freshActive) active.textContent = freshActive.textContent;
      /* valores que deixariam a grelha vazia ficam desligados, como na primeira pintura */
      form.querySelectorAll('input[type="checkbox"].engram-filters__input').forEach((input) => {
        const match = fresh.querySelector(`input[name="${CSS.escape(input.name)}"][value="${CSS.escape(input.value)}"]`);
        if (match) input.disabled = match.disabled && !input.checked;
      });
    } catch (error) {
      /* sem contagem nova o botão continua a aplicar o que está escolhido */
    } finally {
      if (request === countRequest && submit) submit.removeAttribute('aria-busy');
    }
  };

  const queueCount = (form) => {
    clearTimeout(countTimer);
    countTimer = setTimeout(() => updateCount(form), 150);
  };

  document.addEventListener('change', (event) => {
    const form = event.target.closest?.('[data-engram-filters-form]');
    if (form) {
      if (event.target.name === 'engram-price') lastPrice = event.target.value;
      queueCount(form);
    }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('[data-engram-filters-form]');
    if (!form) return;
    event.preventDefault();
    apply(serialize(form));
  });

  /* ---------- 3 · Aplicar: cabeçalho + grelha, sem recarregar ---------- */

  const showSkeleton = (section) => {
    const container = section?.querySelector('[id^="ProductGridContainer--"]');
    if (!container || container.querySelector('.engram-skeleton')) return;
    const list = document.createElement('ul');
    list.className = 'engram-skeleton';
    list.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 8; index += 1) {
      const item = document.createElement('li');
      item.className = 'engram-skeleton__card';
      item.innerHTML = '<span class="engram-skeleton__media"></span><span class="engram-skeleton__line"></span><span class="engram-skeleton__line engram-skeleton__line--short"></span>';
      list.appendChild(item);
    }
    container.prepend(list);
    section.classList.add('engram-is-loading');
  };
  const hideSkeleton = (section) => {
    section?.classList.remove('engram-is-loading');
    section?.querySelectorAll('.engram-skeleton').forEach((list) => list.remove());
  };

  const swap = (id, markup) => {
    const target = document.getElementById(id);
    if (!target || !markup) return;
    const fresh = new DOMParser().parseFromString(markup, 'text/html').getElementById(id);
    if (fresh) target.innerHTML = fresh.innerHTML;
  };

  /* a linha de filtros à vista depois de aplicar, se a página estava mais abaixo */
  const revealBar = () => {
    const bar = document.querySelector('[data-engram-collection-bar]');
    if (!bar) return;
    const top = bar.getBoundingClientRect().top;
    if (top >= 0) return;
    const header = document.querySelector(MOBILE.matches ? '#shopify-section-mobile-header' : '#shopify-section-header');
    const offset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    window.scrollTo(0, window.scrollY + top - offset);
  };

  let applyRequest = 0;
  const apply = async (params, { push = true } = {}) => {
    const element = root();
    const section = grid();
    if (!element) return;
    if (!section) {
      window.location.assign(withParams(element.dataset.url, params));
      return;
    }
    const request = ++applyRequest;
    const headerId = `shopify-section-${element.dataset.sectionId}`;
    const gridId = section.id;
    const refocus = current && current.name === 'filters' ? '[data-engram-open="filters"]' : current && current.name === 'sort' ? '[data-engram-open="sort"]' : null;
    showSkeleton(section);
    say(element.dataset.loading);
    const closing = current ? close({ returnFocus: false, applied: true }) : Promise.resolve();
    try {
      const response = await fetch(withParams(element.dataset.url, params, `sections=${element.dataset.sectionId},${sectionIdOf(section)}`));
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      await closing;
      if (request !== applyRequest) return;
      swap(headerId, data[element.dataset.sectionId]);
      swap(gridId, data[sectionIdOf(section)]);
      hideSkeleton(section); /* a marca «a carregar» vive na secção, que não é trocada: sem isto as peças novas ficavam escondidas */
      if (push) window.history.pushState({ searchParams: params }, '', withParams(element.dataset.url, params));
      try {
        if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = params; /* o «voltar» do tema compara com isto */
      } catch (error) { /* sem o JS de filtros do tema */ }
      say('');
      revealBar();
      if (refocus) document.querySelector(refocus)?.focus({ preventScroll: true });
    } catch (error) {
      await closing;
      if (request !== applyRequest) return;
      hideSkeleton(section);
      say(element.dataset.error);
    }
  };

  /* «voltar»/«avançar» do browser: a grelha é o tema que a repõe (assets/facets.js); o cabeçalho, aqui */
  window.addEventListener('popstate', async () => {
    const element = root();
    if (!element) return;
    if (current) closeNow();
    const params = window.location.search.slice(1);
    const themeHandlesGrid = !!customElements.get('facet-filters-form') && !!document.querySelector('facet-filters-form');
    const section = grid();
    const ids = [element.dataset.sectionId];
    if (!themeHandlesGrid && section) {
      ids.push(sectionIdOf(section));
      showSkeleton(section);
    }
    try {
      const response = await fetch(withParams(element.dataset.url, params, `sections=${ids.join(',')}`));
      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      ids.forEach((id) => swap(`shopify-section-${id}`, data[id]));
      hideSkeleton(section);
    } catch (error) {
      window.location.reload();
    }
  });

  /* ---------- 4 · Quick-add ---------- */

  const quickAdd = async (cta) => {
    if (cta.dataset.busy) return;
    const label = cta.textContent;
    cta.dataset.busy = 'true';
    cta.textContent = cta.dataset.adding;
    const cart = document.querySelector('cart-drawer');
    const payload = { items: [{ id: Number(cta.dataset.engramQuickAdd), quantity: 1 }] };
    /* classe de produção para o Pod Hero (S0: só há quick-add em peças sem personalização; snippets/engram-sla-class) */
    if (cta.dataset.engramSla) payload.items[0].properties = { _sla_class: cta.dataset.engramSla };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      payload.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
      payload.sections_url = window.location.pathname;
    }
    try {
      const response = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'cart');
      if (cart && typeof cart.renderContents === 'function' && data.sections) {
        const opens = cart.classList.contains('open-after-adding');
        cart.renderContents(data);
        if (!opens && typeof cart.open === 'function') cart.open();
      } else {
        window.location.assign((window.routes && window.routes.cart_url) || '/cart');
        return;
      }
      // PUB_SUB_EVENTS é um «const» do assets/global.js: existe pelo nome, não como window.PUB_SUB_EVENTS (testes de 19 Out)
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'engram-quick-add' });
    } catch (error) {
      /* sem saco não se perde o cliente: segue para a página da peça, onde está o botão de compra */
      const link = cta.closest('a[href]');
      if (link) {
        window.location.assign(link.href);
        return;
      }
    } finally {
      cta.textContent = label;
      delete cta.dataset.busy;
    }
  };

  /* ---------- Cliques ---------- */

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;

      /* fase de captura: chega antes do link do cartão */
      const quick = event.target.closest('[data-engram-quick-add]');
      if (quick) {
        event.preventDefault();
        event.stopPropagation();
        quickAdd(quick);
        return;
      }

      /* tocar no header do telemóvel (menu, pesquisa, saco) com uma folha aberta: o que abre entra por cima dela */
      if (current && !current.closing && event.target.closest('#shopify-section-mobile-header')) {
        close({ returnFocus: false, behind: true });
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target;

    const opener = target.closest('[data-engram-open]');
    if (opener) {
      event.preventDefault();
      /* o mesmo botão abre e fecha (o painel de ordenar do desktop fica aberto por baixo dele) */
      if (current && !current.closing && current.trigger === opener) close();
      else open(opener.dataset.engramOpen, opener);
      return;
    }

    /* painel de ordenar do desktop: um clique fora fecha-o */
    if (current && current.popover && !current.closing && !current.panel.contains(target)) {
      close({ returnFocus: false });
    }

    if (target.closest('[data-engram-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const clear = target.closest('[data-engram-clear]');
    if (clear) {
      const form = clear.closest('[data-engram-filters-form]');
      if (!form) return;
      form.querySelectorAll('.engram-filters__input').forEach((input) => { input.checked = false; });
      lastPrice = null;
      queueCount(form);
      return;
    }

    /* uma faixa de preço já escolhida desmarca-se com outro toque */
    if (target.matches('input[name="engram-price"]')) {
      if (target.value === lastPrice) {
        target.checked = false;
        lastPrice = null;
        queueCount(target.form);
      }
      return;
    }

    const sortOption = target.closest('[data-engram-sort-value]');
    if (sortOption) {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.set('sort_by', sortOption.dataset.engramSortValue);
      if (sortOption.getAttribute('aria-pressed') === 'true') {
        close();
        return;
      }
      apply(params.toString());
      return;
    }

    const link = target.closest('a[data-engram-apply]');
    if (link && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
      event.preventDefault();
      apply(new URL(link.href, window.location.href).search.slice(1));
    }
  });

  /* ---------- 5 · Contagem e «mostrar mais» ---------- */

  let pageSize = 0;
  const fill = (template, values) => template.replace(/\[(\w+)\]/g, (_, key) => values[key]);

  const renderMore = () => {
    const section = grid();
    const bar = document.querySelector('[data-engram-collection-bar]');
    if (!section || !bar) return;
    const text = bar.dataset;
    const wrapper = section.querySelector('.pagination-wrapper');
    const button = wrapper?.querySelector('infinite-scroll button');
    if (!button || wrapper.querySelector('infinite-scroll.loading')) return;

    const shown = section.querySelectorAll('.product-grid > li').length;
    /* o total é o do cabeçalho (peças com os filtros escolhidos); a contagem do tema diz «21 de 24» com filtros */
    const total = Number(text.total);
    if (!pageSize || shown < pageSize) pageSize = shown;
    const next = Math.min(pageSize, total - shown);
    if (next < 1) return;

    let count = wrapper.querySelector('.engram-collection-more__count');
    if (!count) {
      count = document.createElement('p');
      count.className = 'engram-collection-more__count engram-caption';
      wrapper.prepend(count);
    }
    const countLabel = fill(text.showing, { shown, total });
    const moreLabel = fill(next === 1 ? text.moreOne : text.moreOther, { count: next });
    if (count.textContent !== countLabel) count.textContent = countLabel;
    if (button.textContent.trim() !== moreLabel) {
      button.textContent = moreLabel;
      button.setAttribute('aria-label', moreLabel);
    }
  };

  const start = () => {
    const section = grid();
    if (!section) return;
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; renderMore(); });
    }).observe(section, { childList: true, subtree: true });
    renderMore();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
